import { useMemo, useState } from 'react';

import { parseJsonLogicToConditions } from '../common/parseJsonLogicForEdit';

import { autoscaleConditionOperators, autoscaleConditionTypes } from './autoscaleRulesMenuItems';

export type TagItem = (typeof autoscaleConditionTypes)[number];
export type CondItem = (typeof autoscaleConditionOperators)[number];
export type ConditionOperator = { id: string; condition: CondItem | null; value: string[] };
export type Condition = {
  id: string;
  type: TagItem | null;
  conditionOperators: ConditionOperator[];
};

function rid() {
  return Math.random().toString(36).slice(2, 8);
}

export function useAutoscaleRulesBuilder() {
  const [ruleConditions, setRuleConditions] = useState<Condition[]>(() => [
    { id: rid(), type: null, conditionOperators: [{ id: rid(), condition: null, value: [] }] },
  ]);

  const selectedTypeTitles = useMemo(
    () => new Set(ruleConditions.flatMap((r) => (r.type ? [r.type.title] : []))),
    [ruleConditions]
  );

  const addRuleType = () => {
    const hasUnusedType = autoscaleConditionTypes.some((t) => !selectedTypeTitles.has(t.title));
    if (!hasUnusedType) {
      return;
    }
    setRuleConditions((prev) => [
      ...prev,
      { id: rid(), type: null, conditionOperators: [{ id: rid(), condition: null, value: [] }] },
    ]);
  };

  const removeRuleType = (id: string) => {
    setRuleConditions((prev) => prev.filter((r) => r.id !== id));
  };

  const updateRuleType = (id: string, next: TagItem) => {
    // Update type and reset condition values to avoid stale selections
    setRuleConditions((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              type: next,
              conditionOperators: r.conditionOperators.map((c) => ({ ...c, value: [] })),
            }
          : r
      )
    );
  };

  const addCondition = (ruleId: string) => {
    setRuleConditions((prev) =>
      prev.map((r) => {
        if (r.id !== ruleId) {
          return r;
        }
        const used = new Set(
          r.conditionOperators.flatMap((c) => (c.condition ? [c.condition.operator] : []))
        );
        const hasUnused = autoscaleConditionOperators.some((c) => !used.has(c.operator));
        if (!hasUnused) {
          return r;
        }
        return {
          ...r,
          conditionOperators: [...r.conditionOperators, { id: rid(), condition: null, value: [] }],
        };
      })
    );
  };

  const removeCondition = (ruleId: string, conditionId: string) => {
    setRuleConditions((prev) =>
      prev.map((r) =>
        r.id === ruleId
          ? { ...r, conditionOperators: r.conditionOperators.filter((c) => c.id !== conditionId) }
          : r
      )
    );
  };

  const updateCondition = (ruleId: string, conditionId: string, next: CondItem) => {
    setRuleConditions((prev) =>
      prev.map((r) => {
        if (r.id !== ruleId) {
          return r;
        }
        return {
          ...r,
          conditionOperators: r.conditionOperators.map((c) =>
            c.id === conditionId ? { ...c, condition: next } : c
          ),
        };
      })
    );
  };

  const setRuleValues = (ruleId: string, conditionId: string, values: string[]) => {
    setRuleConditions((prev) =>
      prev.map((r) =>
        r.id === ruleId
          ? {
              ...r,
              conditionOperators: r.conditionOperators.map((c) =>
                c.id === conditionId ? { ...c, value: values } : c
              ),
            }
          : r
      )
    );
  };

  const canAddRuleType = useMemo(
    () => autoscaleConditionTypes.some((t) => !selectedTypeTitles.has(t.title)),
    [selectedTypeTitles]
  );

  const canAddCondition = (rule: Condition) => {
    // For non-tag types (Cluster, Node group, Instance):
    // Only allow one condition per operator
    // Once a condition is selected, don't allow adding more conditions
    const multiConditionTitles = ['Volume tag', 'Instance tag'];
    if (rule.type && !multiConditionTitles.includes(rule.type.title)) {
      // Check if any condition already has a selected operator
      const hasSelectedCondition = rule.conditionOperators.some((c) => c.condition !== null);
      if (hasSelectedCondition) {
        return false;
      }
    }

    // For Volume tag and Instance tag types: allow multiple conditions with the same operator
    // Always allow adding more conditions for these tag types
    return true;
  };

  const disabledTitlesForType = (rule: Condition) =>
    rule.type
      ? [...selectedTypeTitles].filter((t) => t !== rule.type!.title)
      : [...selectedTypeTitles];

  const disabledTitlesForCondition = (rule: Condition, condition: ConditionOperator) => {
    // For Volume tags and Instance tags: allow duplicate operators (no disabling)
    if (rule.type?.title === 'Volume tag' || rule.type?.title === 'Instance tag') {
      return [];
    }
    // For other types: disable already-used operators
    return rule.conditionOperators
      .flatMap((c) => (c.condition ? [c.condition.operator] : []))
      .filter((t) => t !== (condition.condition?.operator ?? ''));
  };

  const resetBuilder = () => {
    setRuleConditions([
      { id: rid(), type: null, conditionOperators: [{ id: rid(), condition: null, value: [] }] },
    ]);
  };

  const loadFromJsonLogic = (jsonLogic: Record<string, unknown>) => {
    console.log('Loading rule from JSON Logic:', jsonLogic);
    try {
      const parsedConditions = parseJsonLogicToConditions(jsonLogic);
      console.log('Parsed conditions:', parsedConditions);
      setRuleConditions(parsedConditions);
    } catch (error) {
      console.error('Error parsing JSON Logic:', error);
      resetBuilder();
    }
  };

  return {
    // data
    ruleConditions,
    autoscaleConditionTypes,
    autoscaleConditionOperators,
    // actions
    addRuleType,
    removeRuleType,
    updateRuleType,
    addCondition,
    removeCondition,
    updateCondition,
    setRuleValues,
    resetBuilder,
    loadFromJsonLogic,
    // derived
    canAddRuleType,
    canAddCondition,
    disabledTitlesForType,
    disabledTitlesForCondition,
    // valuesForRule,
  };
}

export type AutoscaleRulesBuilderAPI = ReturnType<typeof useAutoscaleRulesBuilder>;
